// Used in districts, activities-container, transactions, explore-resource-container, seat-allocation, users, teacher-classes, teacher-class,
// district, school

// Angular
import { Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";

// Services/Types/Helpers
// RXJS/Store
import * as RouterStore from "#core/router-store";
import { BreadcrumbService } from "@carnegie-learning/data-access-angular-passport";
import { Store, select } from "@ngrx/store";
import { Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";

/**
 * This component shows a breadcrumb trail for available routes the router can navigate to.
 * It subscribes to the router in order to update the breadcrumb trail as you navigate to a component.
 */
@Component({
    selector: "breadcrumb",
    templateUrl: "./breadcrumb.component.html",
    styleUrls: ["./breadcrumb.component.scss"],
    standalone: false
})
export class BreadcrumbComponent implements OnInit, OnChanges, OnDestroy {
    @Input() useBootstrap: true;

    public _urls: string[];
    private ngUnsubscribe = new Subject();

    constructor(
        private router: Router,
        private breadcrumbService: BreadcrumbService,
        private routerStore: Store<RouterStore.RouterStateUrl>
    ) {
        this._urls = [];

        // Populate the breadcrumbs before navigation end occurs, just in case a child component does not trigger it.
        this.initDynamicFriendlyNames();
        this._urls.length = 0; // Fastest way to clear out array
        this.generateBreadcrumbTrail(this.router.url);
    }

    ngOnInit(): void {}

    initStaticFriendlyNames() {
        this.breadcrumbService.hideRouteRegex("^.*?/distributor$");
        this.breadcrumbService.hideRouteRegex("^.*?/schools$");
        this.breadcrumbService.hideRouteRegex("^.*?/teachers$");
        this.breadcrumbService.hideRouteRegex("^.*?/assignments/[0-9]+$");
        this.breadcrumbService.hideRouteRegex("^.*?/teacher$");
        this.breadcrumbService.hideRouteRegex("^.*?/teacher-classes$");
        this.breadcrumbService.hideRouteRegex("^.*?/students$");
        this.breadcrumbService.hideRouteRegex("^.*?/profile$");
        this.breadcrumbService.hideRouteRegex("^.*?/SpineActivity$");
        this.breadcrumbService.hideRouteRegex("^.*?/ResourceActivity$");
        this.breadcrumbService.hideRouteRegex("^.*?/Lesson$");
        this.breadcrumbService.hideRouteRegex("^.*?/Question$");
        this.breadcrumbService.hideRouteRegex("^.*?/Module$");
        this.breadcrumbService.hideRouteRegex("^.*?/Assessment$");
        this.breadcrumbService.hideRouteRegex("^.*?/AvenueTask$");
        this.breadcrumbService.hideRouteRegex("^.*?/StimulusTask$");
        this.breadcrumbService.hideRouteRegex("^.*?/quick-eval$");
        this.breadcrumbService.hideRouteRegex("^.*?/type$");
        this.breadcrumbService.hideRouteRegex("^.*?/create$");
        this.breadcrumbService.hideRouteRegex("^.*?/evaluate$");
        this.breadcrumbService.hideRouteRegex("^.*?/assign$");
        this.breadcrumbService.hideRouteRegex("^.*?/resources$");
        this.breadcrumbService.hideRouteRegex("^.*?/assignable-type$");
        this.breadcrumbService.hideRouteRegex("^.*?/NON_MASTERY$");
        this.breadcrumbService.hideRouteRegex("^.*?/MASTERY$");
        this.breadcrumbService.hideRouteRegex("^.*?/ReadyCheck$");

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/users",
            "Users"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/districts$",
            "Districts"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/seat-allocation$",
            "Seat Allocation"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/transactions$",
            "Transactions"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/assignable-type$",
            "Assignments"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/create/activity$",
            "Create Activity"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/create/lesson$",
            "Create Bundle"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/classes$",
            "Classes"
        );

        // Legacy Support for old path name
        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/teacher-classes$",
            "Classes"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/avenue",
            "Performance"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/stimulus",
            "Journal"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/lessons",
            "Bundle"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/new$",
            "Create"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/edit$",
            "Edit"
        );
        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/explore/[a-z]+/[0-9]+$",
            "iCulture"
        );
        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/all_languages",
            "All Languages"
        );

        this.breadcrumbService.addFriendlyNameForRouteRegex(
            "^.*?/assignments$",
            "Assignments"
        );
    }

    initDynamicFriendlyNames() {
        if (this.router.url.includes("resources")) {
            this.breadcrumbService.hideRouteRegex("^.*?/resource$");
        }

        this.routerStore
            .pipe(select(RouterStore.getRouterState))
            .pipe(first(), takeUntil(this.ngUnsubscribe))
            .subscribe((router) => {
                if (router) {
                    const districtID = router.state.params.districtID;
                    this.breadcrumbService.addFriendlyNameForDistrict(
                        districtID
                    );

                    const schoolID = router.state.params.schoolID;
                    this.breadcrumbService.addFriendlyNameForSchool(schoolID);

                    const teacherID = router.state.params.teacherID;
                    this.breadcrumbService.addFriendlyNameForTeacher(teacherID);

                    const userID = router.state.params.userID;
                    this.breadcrumbService.addFriendlyNameForUser(userID);

                    const courseID = router.state.params.courseID;
                    this.breadcrumbService.addFriendlyNameForCourse(
                        courseID,
                        router.state.url.indexOf("courses") > -1
                    );

                    const studentID = router.state.params.userID;
                    this.breadcrumbService.addFriendlyNameForStudent(studentID);

                    const assignableID = router.state.params.assignableID;
                    const shortAssignableType =
                        router.state.params.shortAssignableType;
                    this.breadcrumbService.addFriendlyNameForAssignable(
                        shortAssignableType,
                        assignableID
                    );

                    const questionID = router.state.params.questionID;
                    this.breadcrumbService.addFriendlyNameForQuestion(
                        questionID
                    );

                    const taskDataID = router.state.params.taskDataID;
                    this.breadcrumbService.addFriendlyNameForAvenue(taskDataID);

                    const stimulusID = router.state.params.stimulusID;
                    this.breadcrumbService.addFriendlyNameForStimulus(
                        stimulusID
                    );

                    const lessonID = router.state.params.lessonID;
                    this.breadcrumbService.addFriendlyNameForLesson(lessonID);

                    const spineID = router.state.params.spineID;
                    this.breadcrumbService.addFriendlyNameForResource(spineID);
                }
            });
    }

    ngOnChanges(changes: any): void {
        if (!this._urls) {
            return;
        }

        this._urls.length = 0;
        this.generateBreadcrumbTrail(this.router.url);
    }

    generateBreadcrumbTrail(url: string): void {
        if (!this.breadcrumbService.isRouteHidden(url)) {
            // Add url to beginning of array (since the url is being recursively broken down from full url to its parent)
            this._urls.unshift(url);
        }

        if (url.lastIndexOf("/") > 0) {
            this.generateBreadcrumbTrail(url.substr(0, url.lastIndexOf("/"))); // Find last '/' and add everything before it as a parent route
        }
    }

    navigateTo(url: string): void {
        this.router.navigateByUrl(url);
    }

    friendlyName(url: string): string {
        return !url ? "" : this.breadcrumbService.getFriendlyNameForRoute(url);
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
